import { z } from "zod";
import Fetcher from "../Fetcher";
import { ValidationSchemas } from "../../util/validation";

type ComplianceAuditRequestExport = {
    toExport: ComplianceAuditResult[];
};

type ComplianceAuditExportResponse = {
    asyncRunId: number;
};

/**
 * Keep in sync with the backend
 * services/compliance/ComplianceAuditExport.ts
 */

type CAExportFileEntry = z.infer<typeof ValidationSchemas.ZipEntrySchema>;

const EvidenceTypeSchema = z.enum([
    // Represents columns on the frontend
    "initialRequest",
    "rfq",
    "vendorQuotes",
    "purchaseOrder",
    "approval",
    "vendorAcknowledgement",
    "statusUpdates",
]);

type EvidenceType = z.infer<typeof EvidenceTypeSchema>;

// The zip entry plus what the frontend needs to display the file
const EvidenceEntrySchema = ValidationSchemas.ZipEntrySchema.extend({
    filename: z.string(),
    mimeType: z.string().nullish(),
});

export type EvidenceEntry = z.infer<typeof EvidenceEntrySchema>;

// An entry of evidence is just an array of files that need to be added to the zip
const EvidenceSchema = z.object({
    evidenceType: EvidenceTypeSchema,
    entries: z.array(EvidenceEntrySchema),
});

export const ComplianceAuditResultSchema = z.object({
    poNumber: z.string().min(1),
    poFound: z.boolean(),
    purchaseOrderId: z.number().int().nullish(),
    buyer: z.string().nullish(),
    createdAt: z.string().nullish(),
    evidence: z.array(EvidenceSchema),
});

export type ComplianceAuditResult = z.infer<typeof ComplianceAuditResultSchema>;

/**
 * End sync with backend
 */

// A PO without a number can't go through the evidence lookup, so the list can return a null poNumber
export type ComplianceAuditListResult = Omit<ComplianceAuditResult, "poNumber"> & { poNumber: string | null };

export type ComplianceAuditListResponse = {
    results: ComplianceAuditListResult[];
    nextCursor: number | null;
    total: number;
};

const f = Fetcher.default();

export default {
    getAuditPage: async (params: { cursor?: number; limit?: number }): Promise<ComplianceAuditListResponse> => {
        const searchParams = new URLSearchParams();
        if (params.cursor) searchParams.set("cursor", params.cursor.toString());
        if (params.limit) searchParams.set("limit", params.limit.toString());

        return await f.get<ComplianceAuditListResponse>(`/compliance-export/list?${searchParams.toString()}`);
    },
    getAuditExportData: async (poNumbers: string[]): Promise<ComplianceAuditResult[]> => {
        return await f.post<ComplianceAuditResult[]>("/compliance-export/export-data", { body: { poNumbers } });
    },
    requestExport: async (params: ComplianceAuditRequestExport) => {
        const url = `/compliance-export/export-zip`;
        // TODO: switch this back
        // const url = `/admin/purchase-orders/export-zip`;
        const res = await f.post<ComplianceAuditExportResponse>(url, { body: params });
        return res;
    },
    getEvidenceFileUrl: async (fileUploadId: number) => {
        return await f.get<{ url: string; expiresOn: string }>(`/compliance-export/files/${fileUploadId}/url`);
    },
};
